import React, { useState } from 'react';
import type { CrudAddModalProps } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Container, Group, Stack } from '@mantine/core';
import { modals } from '@mantine/modals';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { ProductListFragment } from '../../graphql/graphql';
import type { SearchProductProps } from '../products/SearchProduct';
import SearchProduct from '../products/SearchProduct';

type OnCreatedCallback<T> = {
	callback: (entity: T | T[]) => void;
}['callback'];

export type AddProductAssociationModalProps<T> = Omit<CrudAddModalProps<T, T>, 'onCreated'> & {
	entityId: string;
	onAddProduct: (entityId: string, productIds: string | string[]) => Promise<T | T[]>;
	onCreated?: OnCreatedCallback<T>;
	errorMessage: string;
	multiple?: boolean;
	excludeIds?: string[];
};

export default function AddProductAssociationModal<T>(props: AddProductAssociationModalProps<T>) {
	const { modalId, entityId, onAddProduct, errorMessage, onCancel, onCreated, multiple = false, excludeIds } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const [selectedProducts, setSelectedProducts] = useState<ProductListFragment[]>([]);

	const handleProductSelected: SearchProductProps['onSelected'] = (products) => {
		if (!excludeIds?.length) {
			setSelectedProducts(products);
			return;
		}

		const excludedIdSet = new Set(excludeIds);
		setSelectedProducts(products.filter((product) => !excludedIdSet.has(product.id)));
	};

	const handleAddProduct = async () => {
		if (!selectedProducts.length) {
			return;
		}
		const loader = addLoader();
		try {
			const productIds = selectedProducts.map((product) => product.id);
			const created = await onAddProduct(entityId, multiple ? productIds : productIds[0]);
			onCreated?.(created);
		} catch (err) {
			NotificationUtils.showError(err as Error, errorMessage);
		} finally {
			removeLoader(loader);
			modals.close(modalId);
		}
	};

	const handleCancel = () => {
		onCancel?.();
		modals.close(modalId);
	};

	return (
		<Container>
			<Stack>
				<SearchProduct onSelected={handleProductSelected} allowMultiple={multiple} excludeIds={excludeIds} />
				<Group justify="flex-end" mt="md">
					<Button type="button" variant="subtle" onClick={handleCancel} disabled={isLoading}>
						Cancel
					</Button>
					<Button type="submit" disabled={isLoading || !selectedProducts.length} onClick={handleAddProduct}>
						{multiple ? 'Assign products' : 'Assign product'}
					</Button>
				</Group>
			</Stack>
		</Container>
	);
}
